import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Logo, Check } from '../components/Icons.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';

const passages = [
  {
    id: 'vitd',
    title: 'Vitamin D and the immune system',
    text: `Vitamin D is best known for its role in calcium absorption and bone health, but a growing body of research suggests it also modulates the immune system. Receptors for the active form of vitamin D have been identified on most cells involved in the immune response, including macrophages and T-cells. In observational studies, low serum levels of vitamin D have been associated with an increased risk of respiratory infections and several autoimmune conditions, including multiple sclerosis. However, large randomised trials of vitamin D supplementation have produced mixed results: a 2019 trial of monthly high-dose vitamin D in healthy adults found no significant reduction in respiratory infection rates, while a 2020 meta-analysis of trials using daily or weekly low-dose supplements found a modest protective effect, particularly in those who were deficient at baseline. The current consensus among researchers is that vitamin D supplementation is most useful as a corrective measure in deficient populations, rather than as a general preventive intervention. There is no agreed serum threshold that defines deficiency across all clinical guidelines.`,
    questions: [
      {
        q: 'Vitamin D receptors have been found on macrophages.',
        answer: 'T',
        rationale: 'Directly stated: "Receptors for the active form of vitamin D have been identified on most cells involved in the immune response, including macrophages and T-cells."'
      },
      {
        q: 'High-dose monthly vitamin D supplementation reliably reduces respiratory infections in healthy adults.',
        answer: 'F',
        rationale: 'The 2019 trial cited "found no significant reduction in respiratory infection rates" — directly contradicting the claim.'
      },
      {
        q: 'Vitamin D deficiency is the leading cause of multiple sclerosis.',
        answer: 'CT',
        rationale: 'The passage says low vitamin D is "associated with" MS — association is not causation, and the passage doesn\'t claim it\'s the leading cause. Can\'t tell from the text.'
      },
      {
        q: 'All clinical guidelines agree on the serum level that defines vitamin D deficiency.',
        answer: 'F',
        rationale: 'Directly contradicted: "There is no agreed serum threshold that defines deficiency across all clinical guidelines."'
      }
    ]
  },
  {
    id: 'greenspace',
    title: 'Urban green space and mental health',
    text: `A 2018 longitudinal study tracked 9,000 adults in three UK cities over a decade. Participants who lived within 300 metres of an urban park reported, on average, 12% fewer symptoms of depression at follow-up than those without nearby green space, after controlling for income, age, and prior mental health diagnoses. The effect was strongest in residents of high-density housing. The researchers proposed three plausible mechanisms: increased physical activity, social interaction in shared outdoor space, and direct exposure to greenery, which other work has linked to reductions in cortisol. The study did not measure cortisol directly, and could not establish which mechanism predominated. A separate Dutch study published the same year found a similar correlation, although the magnitude was smaller. Neither study was a randomised controlled trial, and both authors acknowledged that unmeasured factors — such as the willingness of certain personality types to move closer to parks — could partly explain the observed effects.`,
    questions: [
      {
        q: 'The UK study controlled for income when analysing depression symptoms.',
        answer: 'T',
        rationale: 'Directly stated: "...after controlling for income, age, and prior mental health diagnoses."'
      },
      {
        q: 'The UK study found that cortisol levels were lower in residents living near parks.',
        answer: 'F',
        rationale: '"The study did not measure cortisol directly" — so it cannot have found a difference in cortisol. The cortisol link comes from other (unspecified) work.'
      },
      {
        q: 'The Dutch study found a stronger green-space effect than the UK study.',
        answer: 'F',
        rationale: 'The Dutch study\'s magnitude was "smaller" — not stronger. Directly contradicted.'
      },
      {
        q: 'The UK study proves that proximity to parks reduces depression.',
        answer: 'CT',
        rationale: 'The study is observational and acknowledges unmeasured confounders ("certain personality types to move closer to parks"). The passage does not claim proof — and we can\'t tell whether causation has been established from this text alone.'
      }
    ]
  },
  {
    id: 'penicillin',
    title: 'Penicillin and antibiotic resistance',
    text: `Alexander Fleming discovered penicillin in 1928 after returning from holiday to find that a stray Penicillium mould had contaminated one of his staphylococcus cultures and was inhibiting bacterial growth around it. Fleming published the finding but did not pursue large-scale production, citing technical difficulties in extracting the active compound. Mass production became feasible only after Howard Florey and Ernst Chain, working in Oxford during the early 1940s, developed techniques to isolate and stabilise penicillin in usable quantities. By 1945, when Fleming, Florey and Chain were jointly awarded the Nobel Prize in Physiology or Medicine, Fleming himself was already warning that improper use of penicillin could select for resistant bacterial strains. His warning preceded the first documented case of clinical resistance to penicillin, which appeared in the medical literature in 1947. Resistance has continued to spread, and today many common bacterial infections — including some strains of Staphylococcus aureus — no longer respond to first-line penicillins.`,
    questions: [
      {
        q: 'Fleming and Florey worked together in Oxford during the 1940s.',
        answer: 'F',
        rationale: 'Fleming discovered penicillin in 1928. Florey and Chain — not Fleming — developed the production techniques in Oxford in the 1940s. The passage does not say Fleming was in Oxford with them.'
      },
      {
        q: 'Fleming warned about antibiotic resistance before any clinical case of penicillin resistance was documented.',
        answer: 'T',
        rationale: 'His Nobel-lecture warning was in 1945; the first documented clinical resistance appeared in 1947. The passage explicitly states "His warning preceded the first documented case."'
      },
      {
        q: 'Penicillin is no longer used as an antibiotic.',
        answer: 'F',
        rationale: '"Many common bacterial infections... no longer respond to first-line penicillins" — but this implies penicillin is still in use, just that resistance has emerged. Directly contradicted by the implied current use.'
      },
      {
        q: 'Methicillin-resistant Staphylococcus aureus is the most common cause of hospital infection.',
        answer: 'CT',
        rationale: 'The passage mentions some Staphylococcus aureus strains are resistant — but it never says MRSA specifically, never compares prevalence, and never claims it\'s the "most common" hospital infection. Can\'t tell.'
      }
    ]
  }
];

const allQuestions = passages.flatMap((p) => p.questions.map((q) => ({ ...q, passageId: p.id, passageTitle: p.title })));

export default function InferenceBuilder() {
  const [stage, setStage] = useState('intro');
  const [pIdx, setPIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startMs, setStartMs] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const passage = passages[pIdx];
  const question = passage?.questions[qIdx];
  const qKey = passage && question ? `${passage.id}:${qIdx}` : null;

  useEffect(() => {
    if (stage !== 'drill') return;
    const t = setInterval(() => setElapsedMs(Date.now() - startMs), 250);
    return () => clearInterval(t);
  }, [stage, startMs]);

  const start = () => {
    setStage('drill');
    setPIdx(0);
    setQIdx(0);
    setAnswers({});
    setStartMs(Date.now());
    setElapsedMs(0);
  };

  const pick = (choice) => {
    setAnswers({ ...answers, [qKey]: choice });
  };

  const next = () => {
    if (qIdx < passage.questions.length - 1) {
      setQIdx(qIdx + 1);
    } else if (pIdx < passages.length - 1) {
      setPIdx(pIdx + 1);
      setQIdx(0);
    } else {
      setElapsedMs(Date.now() - startMs);
      setStage('done');
    }
  };
  const prev = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else if (pIdx > 0) { setPIdx(pIdx - 1); setQIdx(passages[pIdx - 1].questions.length - 1); }
  };

  const totalQ = allQuestions.length;
  const answered = Object.keys(answers).length;
  const correctCount = useMemo(() => {
    let c = 0;
    passages.forEach((p) => {
      p.questions.forEach((q, i) => {
        const a = answers[`${p.id}:${i}`];
        if (a === q.answer) c++;
      });
    });
    return c;
  }, [answers]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', borderBottom: '1px solid var(--border)', background: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
        <Link to="/practice" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-2)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
          <ArrowLeft width="16" height="16" />Exit drill
        </Link>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--brand-deep)' }}>
          <Logo width={28} height={28} /><strong style={{ fontSize: 15 }}>Inference Builder</strong>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
          {stage === 'drill' && formatTime(elapsedMs)}
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
        {stage === 'intro' && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>VR drill</div>
            <h1 style={{ margin: '6px 0 12px', fontSize: 'clamp(26px, 5vw, 36px)', letterSpacing: '-0.01em' }}>Inference Builder</h1>
            <p style={{ color: 'var(--text-2)', fontSize: 16, maxWidth: 680, lineHeight: 1.6 }}>UCAT-style True / False / Can't Tell. Read each passage, then answer with strict rules:</p>
            <ul style={{ color: 'var(--text-2)', fontSize: 15, lineHeight: 1.8, maxWidth: 680 }}>
              <li><strong style={{ color: 'var(--live-green)' }}>True</strong> — the statement is directly supported by the passage.</li>
              <li><strong style={{ color: '#b91c1c' }}>False</strong> — the statement is directly contradicted by the passage.</li>
              <li><strong style={{ color: '#b45309' }}>Can't tell</strong> — the passage doesn't give enough information to decide.</li>
            </ul>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, maxWidth: 680, marginTop: 18 }}>
              {passages.length} passages · {totalQ} questions · ~28 seconds per question on the real UCAT. Per-question rationale is shown at the end.
            </p>
            <button onClick={start} className="btn btn-primary" style={{ marginTop: 22, fontSize: 16, padding: '12px 22px' }}>
              Start drill <ArrowRight width="16" height="16" />
            </button>
          </>
        )}

        {stage === 'drill' && passage && question && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
              <span style={{ fontWeight: 600 }}>Passage {pIdx + 1} of {passages.length} · Q{qIdx + 1} of {passage.questions.length}</span>
              <span>{answered} of {totalQ} answered</span>
            </div>
            <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ width: `${(answered / totalQ) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 250ms ease' }} />
            </div>

            <div className="inference-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <article className="card" style={{ padding: '22px 24px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Passage</div>
                <h2 style={{ margin: '4px 0 14px', fontSize: 20 }}>{passage.title}</h2>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: 'var(--text)' }}>{passage.text}</p>
              </article>

              <div className="card" style={{ padding: '22px 24px', alignSelf: 'start' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Statement</div>
                <p style={{ margin: '8px 0 18px', fontSize: 18, fontWeight: 600, color: 'var(--text)', lineHeight: 1.5 }}>{question.q}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { v: 'T', label: 'True', color: 'var(--live-green)', bg: '#e7f7ef' },
                    { v: 'F', label: 'False', color: '#b91c1c', bg: '#fee2e2' },
                    { v: 'CT', label: "Can't tell", color: '#b45309', bg: '#fef3c7' }
                  ].map((opt) => {
                    const sel = answers[qKey] === opt.v;
                    return (
                      <button
                        key={opt.v}
                        onClick={() => pick(opt.v)}
                        style={{
                          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                          borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                          border: `2px solid ${sel ? opt.color : 'var(--border)'}`,
                          background: sel ? opt.bg : '#fff',
                          color: sel ? opt.color : 'var(--text-2)',
                          transition: 'all 160ms ease'
                        }}
                      >
                        <span style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${sel ? opt.color : 'var(--border-strong)'}`, display: 'grid', placeItems: 'center' }}>
                          {sel && <span style={{ width: 10, height: 10, borderRadius: '50%', background: opt.color }} />}
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button onClick={prev} disabled={pIdx === 0 && qIdx === 0} style={{
                background: 'none', border: 'none', cursor: (pIdx === 0 && qIdx === 0) ? 'not-allowed' : 'pointer',
                color: (pIdx === 0 && qIdx === 0) ? 'var(--text-muted)' : 'var(--text-2)', fontSize: 14, fontWeight: 600,
                opacity: (pIdx === 0 && qIdx === 0) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6
              }}><ArrowLeft width="14" height="14" />Previous</button>
              <button onClick={next} disabled={!answers[qKey]} className="btn btn-primary" style={{ opacity: answers[qKey] ? 1 : 0.5 }}>
                {pIdx === passages.length - 1 && qIdx === passage.questions.length - 1 ? 'Finish' : 'Next'} <ArrowRight width="14" height="14" />
              </button>
            </div>
          </>
        )}

        {stage === 'done' && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Drill complete</div>
            <h1 style={{ margin: '6px 0 6px', fontSize: 'clamp(30px, 6vw, 40px)' }}>{correctCount} <span style={{ color: 'var(--text-muted)', fontSize: 22, fontWeight: 500 }}>/ {totalQ}</span></h1>
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>{formatTime(elapsedMs)} · {Math.round((elapsedMs / totalQ) / 1000)}s per question on average</p>

            <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {passages.map((p) => (
                <div key={p.id} className="card">
                  <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>{p.title}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                    {p.questions.map((q, i) => {
                      const a = answers[`${p.id}:${i}`];
                      const correct = a === q.answer;
                      return (
                        <div key={i} style={{ padding: '12px 14px', borderRadius: 10, background: correct ? '#e7f7ef' : '#fee2e2', borderLeft: `4px solid ${correct ? 'var(--live-green)' : '#b91c1c'}` }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: correct ? 'var(--live-green)' : '#b91c1c', color: '#fff' }}>{correct ? 'Correct' : 'Wrong'}</span>
                            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Your answer: {a} · Correct: {q.answer}</span>
                          </div>
                          <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>{q.q}</p>
                          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55 }}><strong>Rationale: </strong>{q.rationale}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <DrillResultBar slug="inference-builder" score={correctCount} maxScore={totalQ} accuracyPct={Math.round((correctCount / totalQ) * 100)} metadata={{ elapsedMs: elapsed }} />

            <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={start} className="btn btn-primary">Run another <ArrowRight width="14" height="14" /></button>
              <Link to="/practice" className="btn btn-secondary">Back to Practice</Link>
              <Link to="/medi" className="btn btn-secondary">Ask Ash about T/F/CT strategy</Link>
            </div>

            {correctCount / totalQ < 0.65 && (
              <TutorNudge
                tutor={tutorFor('inference')}
                reason="T/F/CT is the most-failed VR question type. A VR tutor can map the inference rules in 60 mins — usually worth 200+ UCAT."
                kicker="Inference is leaking marks"
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function formatTime(ms) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
